import { useCallback, useMemo } from 'react'
import { useFetchFromServer } from '../../api/fetchHook'
import { RoleHierarchy } from '../../../../shared/src/data/roleHierarchy/roleHierarchy'
import { Organization } from '../../../../shared/src/data/roleHierarchy/organization'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { roleHierarchyManagementKeyFactory } from './roleHierarchyManagementKeyFactory'
import { UserDetails } from '../../../../shared/src/data/userDetails'

const BASE_URL = process.env.PUBLIC_URL + '/api/management/roleHierarchy'

export const useRoleHierarchyManager = () => {
  const { fetchJsonFromServer, fetchFromServer } = useFetchFromServer()
  const queryClient = useQueryClient()

  const getRoleHierarchiesQuery = useQuery<RoleHierarchy[]>(
    roleHierarchyManagementKeyFactory.all,
    useCallback(
      () => fetchJsonFromServer<RoleHierarchy[]>(BASE_URL),
      [fetchJsonFromServer],
    ),
    {
      select: (data) => {
        return (
          data?.sort((a, b) =>
            a.organization.name > b.organization.name ? 1 : -1,
          ) || []
        )
      },
    },
  )

  const getUserRoleHierarchyQuery = useQuery<RoleHierarchy>(
    roleHierarchyManagementKeyFactory.currentUser(),
    useCallback(
      () => fetchJsonFromServer<RoleHierarchy>(BASE_URL + '/currentUser'),
      [fetchJsonFromServer],
    ),
  )

  const updateUserRoleHierarchyMutation = useMutation<
    Response,
    unknown,
    RoleHierarchy
  >(
    useCallback(
      (roleHierarchy: RoleHierarchy) =>
        fetchFromServer(BASE_URL, {
          method: 'POST',
          body: JSON.stringify({ roleHierarchy }),
          headers: { 'Content-Type': 'application/json' },
        }),
      [fetchFromServer],
    ),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(
          roleHierarchyManagementKeyFactory.currentUser(),
        ),
    },
  )

  const createUserMutation = useMutation<Response, unknown, UserDetails>(
    useCallback(
      (userDetails: UserDetails) =>
        fetchFromServer(BASE_URL + '/createUser', {
          method: 'POST',
          body: JSON.stringify(userDetails),
          headers: { 'Content-Type': 'application/json' },
        }),
      [fetchFromServer],
    ),
  )

  const createRoleHierarchyMutation = useMutation<
    Response,
    unknown,
    { organizationName: string; branchName: string }
  >(
    useCallback(
      (hierarchyDetails: { organizationName: string; branchName: string }) =>
        fetchFromServer(BASE_URL + '/createRoleHierarchy', {
          method: 'POST',
          body: JSON.stringify(hierarchyDetails),
          headers: { 'Content-Type': 'application/json' },
        }),
      [fetchFromServer],
    ),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(roleHierarchyManagementKeyFactory.all),
    },
  )

  const createBranchMutation = useMutation<
    Response,
    unknown,
    { organization: Organization; branchName: string }
  >(
    useCallback(
      (branchDetails: { organization: Organization; branchName: string }) =>
        fetchFromServer(BASE_URL + '/createBranch', {
          method: 'POST',
          body: JSON.stringify(branchDetails),
          headers: { 'Content-Type': 'application/json' },
        }),
      [fetchFromServer],
    ),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(roleHierarchyManagementKeyFactory.all),
    },
  )

  return useMemo(
    () => ({
      getRoleHierarchiesQuery,
      getUserRoleHierarchyQuery,
      updateUserRoleHierarchyMutation,
      createUserMutation,
      createRoleHierarchyMutation,
      createBranchMutation,
    }),
    [
      getRoleHierarchiesQuery,
      getUserRoleHierarchyQuery,
      updateUserRoleHierarchyMutation,
      createUserMutation,
      createRoleHierarchyMutation,
      createBranchMutation,
    ],
  )
}
